import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer";
import DropdownMenu from "../../components/DropdownMenu";
import SEO from "../../context/SEO";
import styles from "./style.module.scss";

function Delivery() {
  const [showElements, setShowElements] = useState("full-opacity");
  const prevScrollPos = useRef(window.scrollY);
  const [vh, setVh] = useState(window.innerHeight * 0.01);

  useEffect(() => {
    const updateVh = () => setVh(window.innerHeight * 0.01);
    updateVh();
    window.addEventListener("resize", updateVh);
    return () => window.removeEventListener("resize", updateVh);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.scrollY;
      const scrollingUp = currentScrollPos < prevScrollPos.current;
      const scrollingDown = currentScrollPos > prevScrollPos.current;
      const atTop = currentScrollPos < 10 * vh;

      if (atTop) {
        setShowElements("full-opacity");
      } else if (scrollingUp) {
        setShowElements("fade-in-up");
      } else if (scrollingDown) {
        setShowElements("fade-out-up");
      }

      prevScrollPos.current = currentScrollPos;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [vh]);

  return (
    <div>
      <SEO title="Cork Matters - Shipping & Delivery" />
      <DropdownMenu showElements={showElements} />

      <div className={styles.pageHeader}>
        <div className={styles.grid}>
          <div className={styles.pageTitle}>
            <h1>Shipping &amp; Delivery</h1>
          </div>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.grid}>
          <div className={styles.mainColumn}>

            <section className={styles.section}>
              <h2>Our Carriers</h2>
              <p>
                We ship all orders using <strong>CTT</strong> (Correios de
                Portugal) and <strong>MBE Ship</strong> (Mail Boxes Etc.), two
                reliable carriers serving Portugal and the European Union. The
                carrier assigned to your order will depend on your location and
                the most practical option available at the time of dispatch.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Delivery Areas</h2>
              <p>
                We currently deliver within <strong>Portugal</strong> and across
                the <strong>European Union</strong>. If you are located outside
                the EU and would like to enquire about shipping, please{" "}
                <Link to="/contact" className={styles.inlineLink}>
                  contact us
                </Link>{" "}
                directly.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Shipping Costs</h2>

              <div className={styles.carrierBlock}>
                <h3>CTT</h3>
                <p>
                  Shipping costs via CTT are calculated based on the weight and
                  destination of your order. You can consult the full CTT price
                  table directly on their website:
                </p>
                <a
                  href="https://appserver.ctt.pt/SSC_SelfServiceContract/PriceTable"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.externalLink}
                >
                  View CTT Price Table
                </a>
              </div>

              <div className={styles.carrierBlock}>
                <h3>MBE Ship</h3>
                <p>
                  MBE Ship pricing is calculated at checkout based on your
                  delivery address. In some cases, MBE Ship may offer a more
                  competitive rate than CTT — if so, both options will be
                  presented for you to choose from during the checkout process.
                </p>
              </div>
            </section>

            <section className={styles.section}>
              <h2>Processing &amp; Delivery Times</h2>
              <p>
                Orders are typically processed within <strong>1–3 business days</strong> of
                payment confirmation. Estimated delivery times after dispatch:
              </p>
              <ul className={styles.list}>
                <li>
                  <strong>Portugal (mainland):</strong> 2–4 business days
                </li>
                <li>
                  <strong>Portugal (islands — Azores &amp; Madeira):</strong> 4–8 business days
                </li>
                <li>
                  <strong>European Union:</strong> 5–10 business days
                </li>
              </ul>
              <p>
                These are estimates and may vary during peak periods or due to
                circumstances beyond our control. You will receive a confirmation
                email with tracking information once your order has been
                dispatched.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Order Tracking</h2>
              <p>
                Once your order has been dispatched, you will receive an email
                containing your tracking number and a link to follow your
                shipment. If you have not received this within 5 business days
                of placing your order, please{" "}
                <Link to="/contact" className={styles.inlineLink}>
                  get in touch
                </Link>
                .
              </p>
            </section>

            <section className={styles.section}>
              <h2>Damaged or Lost Parcels</h2>
              <p>
                If your order arrives damaged, please photograph the packaging
                and the product before use and contact us within{" "}
                <strong>48 hours</strong> of receipt. In the event of a lost
                parcel, please allow the full estimated delivery window to pass
                before reaching out, as delays can occur in transit.
              </p>
              <p>
                Contact us at{" "}
                <a
                  href="mailto:corkmatters.online@gmail.com"
                  className={styles.inlineLink}
                >
                  corkmatters.online@gmail.com
                </a>{" "}
                and we will work with the carrier to resolve the issue as quickly
                as possible.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Returns</h2>
              <p>
                We accept returns within <strong>14 days</strong> of delivery,
                in accordance with EU consumer law. Items must be returned in
                their original condition and packaging.
              </p>
              <p>
                Please note that <strong>return shipping costs are the responsibility of
                the customer</strong>. We recommend using a tracked service, as we
                cannot accept liability for items lost in transit.
              </p>
              <p>
                To initiate a return, please contact us at{" "}
                <a
                  href="mailto:corkmatters.online@gmail.com"
                  className={styles.inlineLink}
                >
                  corkmatters.online@gmail.com
                </a>{" "}
                with your order number and reason for return. Once we receive
                and inspect the item, a refund will be processed within 14
                business days.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Contact</h2>
              <p>
                For any questions about your order or delivery, please visit our{" "}
                <Link to="/contact" className={styles.inlineLink}>
                  contact page
                </Link>{" "}
                or email us at{" "}
                <a
                  href="mailto:corkmatters.online@gmail.com"
                  className={styles.inlineLink}
                >
                  corkmatters.online@gmail.com
                </a>
                .
              </p>
            </section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Delivery;
